import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'react-bootstrap';
import { KeyRound } from 'lucide-react';
import { UseKeyRegisterReturn, useKeyRegister } from '../../hooks/keyRegister';
import { isOverdue, keysHeldBy, sortKeysBySerial } from '../../Helpers/keyRegisterUtils';
import { KeyHolder, keyStatusLabel } from '../../types/keyRegister';
import Skeleton from '../../Components/Skeleton';
import SectionTitle from '../WorkOrders/components/shared/SectionTitle';
import { keyRegisterLink, keySerialLabel, overdueKeysSentence } from './keyRegisterLabels';
import './KeyRegister.css';

interface HeldKeysSectionProps {
    holder: KeyHolder;
    /** `page`: an h3 section (tenant page); `card`: the settings user-detail card look (external users). */
    variant?: 'page' | 'card';
}

/** The part of the register the list reads; a page that already holds the register passes it in. */
export type HeldKeysRegister = Pick<UseKeyRegisterReturn, 'keys' | 'typesByKey' | 'today' | 'loading' | 'error'>;

/**
 * "Nycklar": the keys a tenant or external user holds right now, overdue first, each linking to the
 * register. Shows the register it is given; `HeldKeysSection` loads it itself.
 */
export const HeldKeysList: React.FC<HeldKeysSectionProps & { register: HeldKeysRegister }> = ({ holder, variant = 'page', register }) => {
    const { keys, typesByKey, today, loading, error } = register;
    // Callers pass a fresh holder object each render; memo on its parts.
    const { kind: holderKind, key: holderKey } = holder;
    const held = useMemo(() => {
        const mine = sortKeysBySerial(keysHeldBy(keys, { kind: holderKind, key: holderKey }))
            .map(key => ({ key, overdue: isOverdue(key, today) }));
        return [...mine.filter(k => k.overdue), ...mine.filter(k => !k.overdue)];
    }, [keys, holderKind, holderKey, today]);
    const overdueCount = held.filter(k => k.overdue).length;

    const heading = `Nycklar${held.length > 0 ? ` (${held.length})` : ''}`;
    const content = (
        <>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? (
                // The empty text's own line, so nothing moves when the keys land.
                <p className="text-muted mb-0" aria-busy="true"><Skeleton inline width="8em" /></p>
            ) : held.length === 0 ? (
                <p className="text-muted mb-0">Inga nycklar ute.</p>
            ) : (
                <>
                    {overdueCount > 0 && (
                        <Alert variant="warning" className="held-keys-alert">
                            {overdueKeysSentence(overdueCount)}
                        </Alert>
                    )}
                    <ul className="held-keys-list">
                        {held.map(({ key, overdue }) => (
                            <li key={key.key} className={`held-key-card${overdue ? ' held-key-card--overdue' : ''}`}>
                                <div className="held-key-main">
                                    <span className="held-key-name">{typesByKey[key.typeKey]?.name ?? 'Okänd nyckeltyp'}</span>
                                    <span className="held-key-serial">{keySerialLabel(key)}</span>
                                </div>
                                <div className="held-key-facts">
                                    <span>{keyStatusLabel(key)}</span>
                                    {key.dueDate && <span>Åter senast {key.dueDate}</span>}
                                    {overdue && <span className="key-overdue-badge">Försenad</span>}
                                </div>
                                <Link to={keyRegisterLink(key.key)} className="held-key-link">
                                    <KeyRound size={14} className="me-1" aria-hidden="true" />Visa i nyckelregistret
                                </Link>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </>
    );

    if (variant === 'card') {
        return (
            <div className="user-detail-card held-keys">
                <h6 className="user-detail-card-title">{heading}</h6>
                {content}
            </div>
        );
    }
    return (
        <section className="held-keys held-keys--page">
            <SectionTitle icon={KeyRound} title="Nycklar" count={held.length} />
            {content}
        </section>
    );
};

/** HeldKeysList with its own load of the admin-only `keyRegister` root, so render it for admins only. */
const HeldKeysSection: React.FC<HeldKeysSectionProps> = props => {
    const register = useKeyRegister();
    return <HeldKeysList {...props} register={register} />;
};

export default HeldKeysSection;
